import { apolloClient, GET_INITIAL_SCREENING_RESUME_PARSER, WORKER_RESUME_PARSER } from '~/common/apollo'
import { UPDATE_WORKER_PROFILE_MUTATION } from '~/common/apollo/mutations/worker.mutation'
import { action, observable, computed, store } from '~/common/mobx.decorator'
import { notifyStore } from '~/stores'
import { normalizeDegreeLevel } from './helpers'

@store()
class ResumeParseStore {
	@observable isOpenResumeParseDialog = false
	@observable showConfirmUseResumeParse = false
	@observable data = {
		candidate: {},
		education: [],
		workHistory: [],
	}

	@observable loading = true
	@observable error = null

	@observable isScanningResume = false
	@observable isParsedResumeFailed = false
	@observable isStartParsedResume = false
	@observable isParseResumeSuccess = false
	@observable loadResumeParserHandler = undefined
	@observable isSubmitting = false
	@observable submitError = null
	@observable initialScreeningResumeParser = {}

	@action
	setIsOpenResumeParseDialog = (isOpen) => {
		this.isOpenResumeParseDialog = isOpen
	}

	@action
	setShowConfirmUseResumeParse = (show) => {
		this.showConfirmUseResumeParse = show
	}

	@action
	setLoading = (value) => {
		this.loading = value
	}

	@action
	reset = () => {
		this.data = {
			candidate: {},
			education: [],
			workHistory: [],
		}
		this.loading = true
		this.error = null
		this.isScanningResume = false
		this.isParsedResumeFailed = false
		this.isStartParsedResume = false
		this.isParseResumeSuccess = false
		this.loadResumeParserHandler = undefined
		this.isSubmitting = false
		this.submitError = null
		this.initialScreeningResumeParser = {}
	}

	@action loadIntervalResumeParser = async (workerId) => {
		//
		if (this.isScanningResume) {
			return
		}
		if (!this.loadResumeParserHandler) {
			this.isScanningResume = true
			this.isParsedResumeFailed = false
			this.isStartParsedResume = true
			this.isParseResumeSuccess = false
			this.loadResumeParserHandler = setInterval(() => {
				this.fetchParsed(workerId)
			}, 2000)
		}
	}

	@action
	fetchParsed = async (workerId) => {
		// this.loading = true
		this.error = null

		try {
			const response = await apolloClient.query({
				query: WORKER_RESUME_PARSER,
				variables: { workerId },
			})

			// Check status from the workerResumeParser object, not the root response
			const status = response.data?.workerResumeParser?.status
			this.isParseResumeSuccess = status === 'completed'
			this.isParsedResumeFailed = status === 'failed'

			if (status === 'completed' || status === 'failed') {
				if (response.data?.workerResumeParser && status === 'completed') {
					this.setData(response.data.workerResumeParser)
				}

				if (status === 'completed') {
					this.fetInitialScreeningResumeParser(workerId)
				}
				this.isScanningResume = false
				this.loading = false
				this.clearLoadIntervalResumeParser()
			}
		} catch (error) {
			this.loading = false
			this.clearLoadIntervalResumeParser()
			notifyStore.error(error.message)
		}
	}

	clearLoadIntervalResumeParser = () => {
		if (this.loadResumeParserHandler) {
			clearInterval(this.loadResumeParserHandler)
		}
		this.loadResumeParserHandler = undefined
	}

	@action
	setData = (payload) => {
		const { workerAddresses = [], workExperiences = [], workerEducations = [], ...candidateData } = payload
		// Map candidate data according to the new API structure
		const candidate = {
			address1:
				workerAddresses?.length > 0
					? {
							street: workerAddresses[0]?.street || '',
							city: workerAddresses[0]?.city || '',
							state: workerAddresses[0]?.state || '',
							zipcode: workerAddresses[0]?.zipcode || '',
							country: workerAddresses[0]?.country || '',
							formatted_address: workerAddresses[0]?.street || '',
					  }
					: {}, // Address 1 as object for AddressField component
			suiteAptFloor: workerAddresses?.length > 0 ? workerAddresses[0]?.aptNumber || '' : '', // Address 2 (Suite/Apt/Floor) from API
			city: workerAddresses?.length > 0 ? workerAddresses[0]?.city || '' : '',
			state: workerAddresses?.length > 0 ? workerAddresses[0]?.state || '' : '',
			zipcode: workerAddresses?.length > 0 ? workerAddresses[0]?.zipcode || '' : '',
			country: workerAddresses?.length > 0 ? workerAddresses[0]?.country || '' : '',
			// Map additional fields from API
			resume: candidateData.resumeDescription || '',
		}

		// Map work history with original IDs preserved - chỉ map fields có trong GraphQL query
		const workHistory = workExperiences.map((work) => ({
			id: work.id || null, // Preserve original ID or set to null for new items
			company: work.facilityName || '',
			position: work.title || '',
			startDate: work.startDate || '',
			endDate: work.endDate || '',
			address1: work.address1 || '',
			address2: work.address2 || '',
			city: work.city || '',
			state: work.state || '',
			country: work.country || '',
			zipcode: work.zip || '',
		}))

		// Map education with original IDs preserved - chỉ map fields có trong GraphQL query
		const education = workerEducations.map((edu) => ({
			id: edu.id || null, // Preserve original ID or set to null for new items
			school: edu.schoolName || '',
			// degreeLevel is the normalized value that matches masterStore.degreeLevels.
			// degreeName is free-form ("Bachelor of Science in Nursing") and never matches the
			// dropdown, so it is carried through untouched rather than shown in the picker.
			degreeLevel: normalizeDegreeLevel(edu.degreeLevel),
			degreeName: edu.degreeName || '',
			areaOfStudy: edu.major || '',
			startDate: edu.startDate || '',
			endDate: edu.endDate || '',
			graduationDate: edu.graduationDate || '',
			gpa: edu.gpa || '',
			city: edu.city || '',
			state: edu.state || '',
			country: edu.country || '', // Added country field mapping
		}))

		this.data = {
			candidate,
			education,
			workHistory,
		}
	}

	@computed
	get hasData() {
		return Object.keys(this.data.candidate).length > 0 || this.data.education.length > 0 || this.data.workHistory.length > 0
	}

	/**
	 * Transform form data to API format for submission
	 * Handles ID logic: items with null ID are new, items with ID but marked for deletion get _destroy: true
	 * Only maps fields that exist on the form - other fields are commented for future reference
	 */
	transformFormDataToAPI = (formData) => {
		const { candidate, education, workHistory } = formData

		// Transform addresses - only map fields available on form
		const addresses = [
			{
				id: null, // New address from resume parse
				street: candidate.address1?.formatted_address || '', // Address 1 from UI
				aptNumber: candidate.suiteAptFloor || '', // Address 2 from UI (Suite/Apt/Floor)
				city: candidate.city || '',
				state: candidate.state || '',
				zipcode: candidate.zipcode || '',
				country: candidate.country || '',
				stateName: candidate.address1?.state,
				countryName: candidate.address1?.country,
				// Fields not available on form - commented for future reference
				// address2: '', // Not used in current UI
				// default: true,
				// note: '',
				// latitude: null,
				// longitude: null,
			},
		]

		// Transform work experiences - chỉ map fields có trong GraphQL query
		const workExperiences = workHistory
			.map((work) => ({
				id: work.id,
				facilityName: work.company || '',
				title: work.position || '',
				startDate: work.startDate || null,
				endDate: work.endDate || null,
				city: work.city || '',
				state: work.state || '',
				country: work.country || '',
				address1: work.address1 || '',
				address2: work.address2 || '',
				zip: work.zipcode || '',
				// Handle _destroy flag: if item has ID but is being deleted, set _destroy: true
				// Items with null ID that are being deleted should be filtered out entirely
				_destroy: work.id && work._destroy ? true : undefined,
			}))
			.filter((work) => {
				// Filter out items with null ID that are marked for deletion
				// Only include items that either have an ID (existing) or are not marked for deletion (new)
				return work.id !== null || !work._destroy
			})

		// Transform education - chỉ map fields có trong GraphQL query
		const workerEducations = education
			.map((edu) => ({
				id: edu.id,
				schoolName: edu.school || '',
				major: edu.areaOfStudy || '',
				city: edu.city || '',
				state: edu.state || '',
				country: edu.country || '', // Added country field
				degreeLevel: edu.degreeLevel || '',
				degreeName: edu.degreeName || '',
				startDate: edu.startDate || null,
				endDate: edu.endDate || null,
				graduationDate: edu.graduationDate || null,
				gpa: Number(edu.gpa) || 0,
				_destroy: edu.id && edu._destroy ? true : undefined,
			}))
			.filter((edu) => {
				return edu.id !== null || !edu._destroy
			})

		return {
			resumeDescription: candidate.resume || '',
			addresses,
			workExperiences,
			workerEducations,
		}
	}

	/**
	 * Submit resume parse data to API
	 */
	@action
	submitResumeParseData = async (formData) => {
		this.isSubmitting = true
		this.submitError = null

		try {
			// Transform form data to API format
			const apiData = this.transformFormDataToAPI(formData)

			// Call the mutation
			const response = await apolloClient.mutate({
				mutation: UPDATE_WORKER_PROFILE_MUTATION,
				variables: apiData,
			})

			if (response.data?.updateProfile) {
				// Success - close dialog and show success message
				this.isOpenResumeParseDialog = false
				return { success: true, data: response.data.updateProfile }
			} else {
				throw new Error('Failed to update profile')
			}
		} catch (error) {
			this.submitError = error.message || 'Failed to submit resume parse data'
			notifyStore.error(error.message)
			throw error
		} finally {
			this.isSubmitting = false
		}
	}

	@action
	fetInitialScreeningResumeParser = async (workerId) => {
		try {
			const response = await apolloClient.query({
				query: GET_INITIAL_SCREENING_RESUME_PARSER,
				variables: { workerId },
			})

			this.initialScreeningResumeParser = response.data?.initialScreeningResumeParser || {}
		} catch (error) {
			this.loading = false
			notifyStore.error(error.message)
		}
	}

	getInitResumeParserAnswer = (questionName) => {
		return this.initialScreeningResumeParser[questionName]
	}
}

export const resumeParseStore = new ResumeParseStore()
